import { canonicalPurpose } from './manualChartDays.js';

// aggregateLogs.js — modifica os dados de fato:

// Time buckets (mode === 'time'): agrupa os logs em janelas de tempo (intervalMs), mantém apenas o último log de cada bucket como representante, mas com o PNL acumulado de todos os logs daquele bucket. Os outros campos (portfolioPMPRegulars, currentPrice, etc.) vêm simplesmente do último log — não são médias nem agregados.
// Significant filter (mode === 'significant'): filtra os logs, mantendo apenas aqueles onde houve mudança relevante em qty/PMP/preço.
// Reduz o número de pontos de dados e acumula o PNL. É uma amostragem com perda para as demais métricas.

// aggregationLabels.js — lê os logs já processados por aggregateLogs e gera apenas os labels do eixo X do gráfico. Não afeta os dados em si.

// logs brutos
//    → aggregateLogs()  ← reduz pontos + acumula PNL
//        → buildAggregationLabels()  ← só formata labels para exibição

export const DEFAULT_AGG_OPTIONS = {
  intervalMs: 60 * 60 * 1000,
  qtyDelta: 0.001,
  pmpDeltaPct: 0.02,
  priceDelta: 200,
  mode: 'time'
};

const getTimestamp = (log) => {
  const ts = log?.orderData?.timestamp ?? log?.processedAt;
  return Number.isFinite(ts) ? ts : null;
};

const getRebuyQty = (metrics) => {
  const qty = metrics?.pmpRebuyUnits || 0;
  return Number.isFinite(qty) ? qty : 0;
};

export function aggregateLogs(logs, options = {}) {
  const settings = { ...DEFAULT_AGG_OPTIONS, ...options };
  const mode = settings.mode || 'time';
  const useTimeBuckets = mode === 'time';
  const useSignificant = mode === 'significant';

  if (!Array.isArray(logs) || logs.length === 0) return [];

  const candidates = logs
    .filter((log) => log?.metrics)
    .map((log) => ({ log, ts: getTimestamp(log) }))
    .filter(({ ts }) => Number.isFinite(ts))
    .sort((a, b) => a.ts - b.ts);

  if (candidates.length === 0) return [];

  let accumulatedPnl = 0;
  let accumulatedPnlRegular = 0;
  let accumulatedPnlManual = 0;
  let accumulatedPnlHedge = 0;
  let accumulatedDealCount = 0;
  let accumulatedSellManualCount = 0;
  let accumulatedHedgeCount = 0;
  let series;

  if (useTimeBuckets) {
    const bucketed = [];
    const gridOrigin = Math.floor(candidates[0].ts / settings.intervalMs) * settings.intervalMs;
    let currentBucketIdx = 0;
    let lastLog = candidates[0].log;
    let bucketPnlSum = 0;
    let bucketPnlSumRegular = 0;
    let bucketPnlSumManual = 0;
    let bucketPnlSumHedge = 0;
    let bucketDealCount = 0;
    let bucketSellManualCount = 0;
    let bucketHedgeCount = 0;

    for (const { log, ts } of candidates) {
      const pnl = Number(log.metrics?.pnl) || 0;
      const strategy = log.strategy;
      const purpose = canonicalPurpose(log.purpose);
      const idx = Math.floor((ts - gridOrigin) / settings.intervalMs);

      if (idx > currentBucketIdx) {
        accumulatedPnl += bucketPnlSum;
        accumulatedPnlRegular += bucketPnlSumRegular;
        accumulatedPnlManual += bucketPnlSumManual;
        accumulatedPnlHedge += bucketPnlSumHedge;
        accumulatedDealCount += bucketDealCount;
        accumulatedSellManualCount += bucketSellManualCount;
        accumulatedHedgeCount += bucketHedgeCount;
        // Define processedAt como o fim do bucket (gridOrigin + (currentBucketIdx + 1) * intervalMs)
        const bucketEndTs = gridOrigin + (currentBucketIdx + 1) * settings.intervalMs;
        bucketed.push({ ...lastLog, processedAt: bucketEndTs, metrics: { ...lastLog.metrics, pnl: accumulatedPnl, pnlRegular: accumulatedPnlRegular, pnlManual: accumulatedPnlManual, pnlHedge: accumulatedPnlHedge, dealCount: accumulatedDealCount, sellManualCount: accumulatedSellManualCount, hedgeCount: accumulatedHedgeCount } });
        currentBucketIdx = idx;
        bucketPnlSum = pnl;
        bucketPnlSumRegular = strategy === 'regular' ? pnl : 0;
        bucketPnlSumManual = (purpose === 'closeLong' || strategy === 'manual') ? pnl : 0;
        bucketPnlSumHedge = purpose === 'closeShort' ? pnl : 0;
        bucketDealCount = (purpose === 'regularSell' || purpose === 'rebuyBuy') ? 1 : 0;
        bucketSellManualCount = purpose === 'closeLong' ? 1 : 0;
        bucketHedgeCount = purpose === 'closeShort' ? 1 : 0;
      } else {
        bucketPnlSum += pnl;
        if (strategy === 'regular') bucketPnlSumRegular += pnl;
        else if (purpose === 'closeLong' || strategy === 'manual') bucketPnlSumManual += pnl;
        if (purpose === 'closeShort') bucketPnlSumHedge += pnl;
        if (purpose === 'regularSell' || purpose === 'rebuyBuy') bucketDealCount++;
        if (purpose === 'closeLong') bucketSellManualCount++;
        if (purpose === 'closeShort') bucketHedgeCount++;
      }
      lastLog = log;
    }
    accumulatedPnl += bucketPnlSum;
    accumulatedPnlRegular += bucketPnlSumRegular;
    accumulatedPnlManual += bucketPnlSumManual;
    accumulatedPnlHedge += bucketPnlSumHedge;
    accumulatedDealCount += bucketDealCount;
    accumulatedSellManualCount += bucketSellManualCount;
    accumulatedHedgeCount += bucketHedgeCount;
    // Para o último bucket
    const bucketEndTs = gridOrigin + (currentBucketIdx + 1) * settings.intervalMs;
    bucketed.push({ ...lastLog, processedAt: bucketEndTs, metrics: { ...lastLog.metrics, pnl: accumulatedPnl, pnlRegular: accumulatedPnlRegular, pnlManual: accumulatedPnlManual, pnlHedge: accumulatedPnlHedge, dealCount: accumulatedDealCount, sellManualCount: accumulatedSellManualCount, hedgeCount: accumulatedHedgeCount } });
    series = bucketed;
  } else {
    series = candidates.map(({ log }) => {
      const pnl = Number(log.metrics?.pnl) || 0;
      const strategy = log.strategy;
      const purpose = canonicalPurpose(log.purpose);
      accumulatedPnl += pnl;
      if (strategy === 'regular') accumulatedPnlRegular += pnl;
      else if (purpose === 'closeLong' || strategy === 'manual') accumulatedPnlManual += pnl;
      if (purpose === 'closeShort') accumulatedPnlHedge += pnl;
      if (purpose === 'regularSell' || purpose === 'rebuyBuy') accumulatedDealCount++;
      if (purpose === 'closeLong') accumulatedSellManualCount++;
      if (purpose === 'closeShort') accumulatedHedgeCount++;
      return { ...log, metrics: { ...log.metrics, pnl: accumulatedPnl, pnlRegular: accumulatedPnlRegular, pnlManual: accumulatedPnlManual, pnlHedge: accumulatedPnlHedge, dealCount: accumulatedDealCount, sellManualCount: accumulatedSellManualCount, hedgeCount: accumulatedHedgeCount } };
    });
  }

  if (!useSignificant) return series;

  const result = [series[0]];
  for (let i = 1; i < series.length; i++) {
    const m = series[i].metrics || {};
    const p = result[result.length - 1].metrics || {};

    const qtyChange =
      Math.abs((m.portfolioVolumeRegulars || 0) - (p.portfolioVolumeRegulars || 0)) +
      Math.abs((m.portfolioVolumeManuals || 0) - (p.portfolioVolumeManuals || 0)) +
      Math.abs(getRebuyQty(m) - getRebuyQty(p));

    const pmpChangePct = Math.max(
      Math.abs((m.portfolioPMPRegulars || 0) - (p.portfolioPMPRegulars || 0)) / (p.portfolioPMPRegulars || 1),
      Math.abs((m.portfolioPMPManuals || 0) - (p.portfolioPMPManuals || 0)) / (p.portfolioPMPManuals || 1)
    );

    const priceChange = Math.abs((m.currentPrice || 0) - (p.currentPrice || 0));

    if (qtyChange >= settings.qtyDelta || pmpChangePct >= settings.pmpDeltaPct || priceChange >= settings.priceDelta) {
      result.push(series[i]);
    }
  }

  return result;
}

const formatLabelDate = (date) => {
  const dayMonth = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit'
  }).format(date);

  const time = new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);

  return { dayMonth, time };
};

const normalizeTimestamp = (value) => {
  if (!Number.isFinite(value)) return null;
  return value < 1000000000000 ? value * 1000 : value;
};

export const getLogTimestamp = (log) => {
  const raw = log?.orderData?.timestamp ?? log?.processedAt ?? log?.timestamp;
  return normalizeTimestamp(raw);
};

export const buildAggregationLabels = (logs, mode) => {
  return logs.map((log) => {
    const ts = getLogTimestamp(log);
    if (!ts) return '';

    const end = formatLabelDate(new Date(ts));

    if (mode === 'time') return `${end.dayMonth} - ${end.time}`;
    if (mode === 'significant') return `Sig ${end.dayMonth} - ${end.time}`;

    return `${end.dayMonth} - ${end.time}`;
  });
};

export const getXAxisTitle = (mode) => {
  if (mode === 'time') return 'Tempo (janela)';
  if (mode === 'significant') return 'Eventos significativos';
  return 'Eventos';
};

export function labelFormatted(ts) {
  const end = formatLabelDate(new Date(ts));
  return `${end.dayMonth} - ${end.time}`;
}